import { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Save, Terminal, Key } from '../../../icons';
import { StyledSelect } from './MenuControls';
import { ProviderIcon } from './AiProviderIcons';
import {
  getOfficeAiSettings,
  saveOfficeAiSettings,
  listAiProviderModels,
  type OfficeAiSettings,
  type AiProviderModel,
  type EnvProvider,
  type CliProvider,
} from '../../../api/aiProviders';
import './AiOfficeProvider.css';

/**
 * **오피스 Provider** — AI Provider 화면 제일 하단의 별도 설정 (2026-08-15, PR5).
 *
 * ## 왜 별도 항목인가
 *
 * 워크스페이스 문서 편집기 안의 AI 는 요청 한 번이 **여러 턴**을 돈다(도구를 부르고 결과를
 * 받아 다시 부른다). 전역 선택이 CLI 계열이면 서버의 CLI 실행 줄이 하나라, 그 8턴 동안
 * 채팅·회의록·아이콘 생성이 전부 뒤에 선다.
 *
 * 그래서 **기존 CLI provider 를 복사한 오피스 전용 항목**(`office.cli.<bin>`)을 만든다.
 * 바이너리도 모델도 원본과 같고, 다른 것은 서버의 **실행 레인** 하나뿐이다 — 같은 도구를
 * 쓰되 줄을 따로 선다. 접두는 서버가 준 값(`cli_prefix`)을 쓴다(양쪽에 적으면 갈린다).
 *
 * API 키(env) provider 는 복사하지 않는다 — HTTP 경로는 동시성이 안전해 줄을 나눌 이유가
 * 없고, 같은 키를 두 이름으로 부르는 혼란만 남는다. 위에 연결된 키를 그대로 고른다.
 * **오피스 전용 API 키를 따로 받아 등록하는 것은 후속**이며, 그때 이 목록에 항목만 는다.
 *
 * ## 미설정 = 종전대로
 *
 * 아무것도 지정하지 않으면 저장 파일이 생기지 않고 chat-server 가 환경변수 → 기본값으로
 * 떨어진다. 즉 **손대지 않으면 지금과 완전히 같다.**
 */

/** 도구(문서 수정)를 가진 편집기 — 한글·평문은 애초에 도구가 없어 목록에 없다. */
const TOOL_EDITORS: Array<{ id: string; label: string }> = [
  { id: 'markdown', label: 'Markdown' },
  { id: 'docs', label: 'Word' },
  { id: 'pdf', label: 'PDF' },
  { id: 'sheets', label: 'Excel' },
  { id: 'slides', label: 'PowerPoint' },
];

/** 서버가 접두를 안 줄 때의 대비값 — 정상 경로에서는 항상 서버 값이 이긴다. */
const FALLBACK_CLI_PREFIX = 'office.cli.';

interface OfficeCandidate {
  /** 저장되는 id — CLI 복사본은 접두가 붙고, env 는 원본 그대로. */
  id: string;
  /** 모델·헬스 조회에 쓰는 **원본** id (복사본은 원본을 가리킨다). */
  sourceId: string;
  name: string;
  vendor: string;
  detail: string;
}

export function AiOfficeProviderPanel({ providers, globalSelectedId }: {
  /** 위 목록이 이미 감지해 둔 provider 들 — 여기서 다시 감지하지 않는다(중복 왕복 방지). */
  providers: { env: EnvProvider[]; cli: CliProvider[] };
  /** 전역 선택 — "지정 없음" 일 때 실제로 무엇이 쓰이는지 보여 준다. */
  globalSelectedId: string | null;
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [providerId, setProviderId] = useState<string | null>(null);
  const [modelId, setModelId] = useState<string>('');
  const [maxTokens, setMaxTokens] = useState<string>('');
  const [toolsEnabled, setToolsEnabled] = useState<boolean>(true);
  const [disabled, setDisabled] = useState<string[]>([]);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [cliPrefix, setCliPrefix] = useState<string>(FALLBACK_CLI_PREFIX);
  const [laneConcurrency, setLaneConcurrency] = useState<number | null>(null);

  const [modelsBySource, setModelsBySource] = useState<
    Record<string, AiProviderModel[] | 'loading' | 'unsupported' | 'error'>
  >({});

  /** CLI 복사본 — 이 화면의 주인공. 감지된 CLI 를 그대로 베끼고 id 에 접두만 붙인다. */
  const cliCandidates = useMemo<OfficeCandidate[]>(() => providers.cli.map((p) => ({
    id: `${cliPrefix}${p.bin}`,
    sourceId: p.id,
    name: p.name,
    vendor: p.vendor,
    detail: `${p.bin}${p.version ? ` · ${p.version}` : ''}`,
  })), [providers.cli, cliPrefix]);

  /** API 키 provider — 복사 없이 그대로 고른다(HTTP 는 동시성이 안전하다). */
  const envCandidates = useMemo<OfficeCandidate[]>(() => providers.env.map((p) => ({
    id: p.id,
    sourceId: p.id,
    name: p.name,
    vendor: p.vendor,
    detail: p.base_url,
  })), [providers.env]);

  const all = useMemo(
    () => [...cliCandidates, ...envCandidates],
    [cliCandidates, envCandidates],
  );
  const chosen = all.find((c) => c.id === providerId) ?? null;
  const globalName = [...providers.env, ...providers.cli].find((p) => p.id === globalSelectedId)?.name ?? null;

  const apply = useCallback((s: OfficeAiSettings) => {
    setProviderId(s.provider_id);
    setModelId(s.model_id ?? '');
    setMaxTokens(s.max_tokens == null ? '' : String(s.max_tokens));
    // null(미설정)은 "사용" 이 기본이다 — 서버 기본값과 같은 뜻으로 보여 준다.
    setToolsEnabled(s.tools_enabled ?? true);
    setDisabled(s.disabled_editors);
    setUpdatedAt(s.updated_at);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getOfficeAiSettings()
      .then((r) => {
        if (cancelled) return;
        apply(r.settings);
        if (r.cli_prefix) setCliPrefix(r.cli_prefix);
        if (r.cli_lane_concurrency) setLaneConcurrency(r.cli_lane_concurrency);
      })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [apply]);

  /** 모델 목록은 **원본 id** 로 묻는다 — 복사본은 서버 목록에 없는 이름이다. */
  const loadModels = useCallback(async (sourceId: string) => {
    setModelsBySource((m) => (m[sourceId] ? m : { ...m, [sourceId]: 'loading' }));
    try {
      const r = await listAiProviderModels(sourceId);
      setModelsBySource((m) => ({ ...m, [sourceId]: r.source === 'unsupported' ? 'unsupported' : r.models }));
    } catch {
      setModelsBySource((m) => ({ ...m, [sourceId]: 'error' }));
    }
  }, []);

  /** 저장 — 카드의 "지정/해제" 와 아래 세부 설정이 같은 한 번의 PUT 을 쓴다. */
  const persist = useCallback(async (next: {
    providerId: string | null;
    modelId: string;
    maxTokens: string;
    toolsEnabled: boolean;
    disabled: string[];
  }, message: string) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const r = await saveOfficeAiSettings({
        provider_id: next.providerId,
        model_id: next.providerId ? (next.modelId || null) : null,
        max_tokens: next.maxTokens.trim() === '' ? null : Number(next.maxTokens),
        // ⚠ 켠 상태도 **명시적으로** 저장한다. 화면이 "사용" 인데 서버 환경변수가 끄고 있으면
        //   보이는 것과 실제가 갈린다 — 저장한 순간부터 이 화면이 그 필드의 주인이다.
        tools_enabled: next.toolsEnabled,
        disabled_editors: next.disabled,
      });
      apply(r.settings);
      setNotice(message);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? '저장 실패');
    } finally {
      setSaving(false);
    }
  }, [apply]);

  const assign = useCallback((cand: OfficeCandidate, model: string) => {
    void persist(
      { providerId: cand.id, modelId: model, maxTokens, toolsEnabled, disabled },
      `오피스 Provider 를 ${cand.name}${model ? ` (${model})` : ''} 로 지정했습니다. 편집기 AI 에 최대 30초 안에 반영됩니다.`,
    );
  }, [disabled, maxTokens, persist, toolsEnabled]);

  const unassign = useCallback(() => {
    void persist(
      { providerId: null, modelId: '', maxTokens, toolsEnabled, disabled },
      '지정을 해제했습니다. 편집기 AI 가 전역 선택을 따릅니다.',
    );
  }, [disabled, maxTokens, persist, toolsEnabled]);

  const invalidTokens = maxTokens.trim() !== ''
    && (!Number.isFinite(Number(maxTokens)) || Number(maxTokens) < 256 || Number(maxTokens) > 200_000);

  return (
    <div className="ait-office">
      <header className="ait-office__head">
        <span className="ait-office__icon"><FileText size={17} /></span>
        <span className="ait-office__title">오피스 Provider</span>
        <span className="ait-office__count">{all.length}</span>
        <span className="ait-office__desc">
          워크스페이스 문서 편집기(Word·Excel·PPT·PDF·Markdown·한글·텍스트) 안의 AI 만 별도로 지정합니다.
        </span>
      </header>

      <p className="ait-office__why">
        편집기 AI 는 요청 한 번이 여러 턴을 돕니다. 전역 선택이 CLI 계열이면 실행 줄이 하나라 그동안
        채팅·회의록·아이콘 생성이 함께 대기합니다. 아래 <b>CLI 도구 기반(복사본)</b>은 같은 도구를 쓰되
        <b> 편집기 전용 실행 줄</b>을 씁니다{laneConcurrency ? ` (동시 실행 ${laneConcurrency}개)` : ''} —
        그래서 편집기 작업이 다른 AI 기능을 세우지 않습니다.
      </p>

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {notice && <div className="ait-office__notice" role="status">{notice}</div>}

      {loading ? (
        <p className="settings-muted">불러오는 중…</p>
      ) : (
        <>
          {/* 현재 상태 — 지정이 없으면 전역을 따른다는 사실을 이름과 함께 보여 준다. */}
          <div className="ait-office__current" data-assigned={providerId ? 'true' : 'false'}>
            <span className="ait-office__current-label">현재 편집기 AI</span>
            {chosen ? (
              <span className="ait-office__current-name">
                {chosen.name}
                {modelId && <code className="ait-office__current-model">{modelId}</code>}
              </span>
            ) : (
              <span className="ait-office__current-name ait-office__current-name--follow">
                지정 없음 — 전역 선택 따름{globalName ? ` (현재: ${globalName})` : ''}
              </span>
            )}
            {providerId && (
              <button type="button" className="ait-office__unassign" disabled={saving} onClick={unassign}>
                지정 해제
              </button>
            )}
          </div>

          {/* 그룹 ①: CLI 도구 기반 복사본 — 이 화면의 주인공 */}
          <div className="ait-office__group">
            <header className="ait-office__group-head">
              <span className="ait-office__group-icon"><Terminal size={15} /></span>
              <span className="ait-office__group-title">CLI 도구 기반 (복사본)</span>
              <span className="ait-office__count">{cliCandidates.length}</span>
              <span className="ait-office__desc">
                위에서 감지된 CLI 를 그대로 복사한 오피스 전용 항목입니다. 편집기 전용 실행 줄을 씁니다.
              </span>
            </header>
            {cliCandidates.length === 0 ? (
              <p className="settings-muted">감지된 CLI 도구가 없습니다.</p>
            ) : (
              <ul className="ait-office__list">
                {cliCandidates.map((c) => (
                  <OfficeProviderCard
                    key={c.id}
                    candidate={c}
                    active={c.id === providerId}
                    activeModel={c.id === providerId ? modelId : ''}
                    models={modelsBySource[c.sourceId] ?? null}
                    busy={saving}
                    onLoadModels={() => void loadModels(c.sourceId)}
                    onAssign={(m) => assign(c, m)}
                    onUnassign={unassign}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* 그룹 ②: API 키 기반 — 복사 없이 그대로 지정 */}
          <div className="ait-office__group">
            <header className="ait-office__group-head">
              <span className="ait-office__group-icon"><Key size={15} /></span>
              <span className="ait-office__group-title">API 키 기반</span>
              <span className="ait-office__count">{envCandidates.length}</span>
              <span className="ait-office__desc">
                HTTP 호출이라 동시 실행이 안전해 복사하지 않습니다. 위에 연결된 키를 그대로 고릅니다.
              </span>
            </header>
            {envCandidates.length === 0 ? (
              <p className="settings-muted">연결된 API 키 provider 가 없습니다.</p>
            ) : (
              <ul className="ait-office__list">
                {envCandidates.map((c) => (
                  <OfficeProviderCard
                    key={c.id}
                    candidate={c}
                    active={c.id === providerId}
                    activeModel={c.id === providerId ? modelId : ''}
                    models={modelsBySource[c.sourceId] ?? null}
                    busy={saving}
                    onLoadModels={() => void loadModels(c.sourceId)}
                    onAssign={(m) => assign(c, m)}
                    onUnassign={unassign}
                  />
                ))}
              </ul>
            )}
            <p className="ait-office__hint">
              오피스 전용 API 키를 따로 받아 등록하는 기능은 후속입니다 — 그때 이 목록에 항목만 늘어납니다.
            </p>
          </div>

          {/* 세부 설정 — provider 와 무관하게 편집기 AI 전체에 걸린다. */}
          <div className="ait-office__group">
            <header className="ait-office__group-head">
              <span className="ait-office__group-title">편집기 AI 세부 설정</span>
            </header>

            <div className="ait-office__row">
              <label className="ait-office__label">최대 토큰</label>
              <input
                className="ait-office__input ait-office__input--num"
                type="number"
                min={256}
                max={200000}
                value={maxTokens}
                placeholder="기본 8000"
                disabled={saving}
                onChange={(e) => setMaxTokens(e.target.value)}
                aria-label="최대 토큰"
              />
              <span className="ait-office__hint">
                한 응답의 상한입니다. 비우면 기본(8000)을 씁니다.
                {invalidTokens && <b className="ait-office__bad"> 256 ~ 200000 사이여야 합니다.</b>}
              </span>
            </div>

            <div className="ait-office__row">
              <span className="ait-office__label">문서 수정 도구</span>
              <label className="svc-switch" title="문서 수정 도구 사용">
                <input
                  type="checkbox"
                  checked={toolsEnabled}
                  disabled={saving}
                  aria-label="문서 수정 도구 사용"
                  onChange={(e) => setToolsEnabled(e.target.checked)}
                />
                <span className="svc-switch__track" />
                <span className="svc-switch__thumb" />
              </label>
              <span className="ait-office__hint">
                끄면 AI 가 문서를 직접 고치지 않고 답변·초안만 냅니다(대화는 그대로 됩니다).
              </span>
            </div>

            {toolsEnabled && (
              <div className="ait-office__row ait-office__row--wrap">
                <span className="ait-office__label">도구를 끌 편집기</span>
                <div className="ait-office__chips">
                  {TOOL_EDITORS.map((e) => {
                    const off = disabled.includes(e.id);
                    return (
                      <label key={e.id} className={`ait-office__chip${off ? ' ait-office__chip--on' : ''}`}>
                        <input
                          type="checkbox"
                          checked={off}
                          disabled={saving}
                          onChange={(ev) => setDisabled((d) => (
                            ev.target.checked ? [...d, e.id] : d.filter((x) => x !== e.id)
                          ))}
                        />
                        {e.label}
                      </label>
                    );
                  })}
                </div>
                <span className="ait-office__hint">
                  체크한 편집기에서만 도구를 끕니다. 한글·평문은 원래 도구가 없어 목록에 없습니다.
                </span>
              </div>
            )}

            <div className="ait-office__actions">
              <button
                type="button"
                className="svc-btn"
                disabled={saving || invalidTokens}
                onClick={() => void persist(
                  { providerId, modelId, maxTokens, toolsEnabled, disabled },
                  '저장했습니다. 편집기 AI 에 최대 30초 안에 반영됩니다.',
                )}
              >
                <Save size={13} /> {saving ? '저장 중…' : '세부 설정 저장'}
              </button>
              <span className="ait-office__hint">
                {updatedAt
                  ? `마지막 저장 ${new Date(updatedAt).toLocaleString('ko-KR')}`
                  : '아직 저장한 적 없음 — 서버 기본 설정을 씁니다.'}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 오피스 Provider 카드 — 위 AI Provider 목록의 카드를 **복사**해 오피스용으로 고친 것.
 *
 * 상류 카드(`ProviderCard`)를 그대로 쓰지 않는 이유: 그 카드는 "전역 활성화" 를 뜻하는 문구와
 * 헬스체크를 갖고 있어 여기서는 의미가 어긋난다(헬스는 같은 원본을 위에서 이미 잴 수 있다).
 * 기존 컴포넌트를 고치면 전역 화면이 함께 흔들리므로 **건드리지 않고 베낀다.**
 */
function OfficeProviderCard({ candidate, active, activeModel, models, busy, onLoadModels, onAssign, onUnassign }: {
  candidate: OfficeCandidate;
  active: boolean;
  activeModel: string;
  models: AiProviderModel[] | 'loading' | 'unsupported' | 'error' | null;
  busy: boolean;
  onLoadModels: () => void;
  onAssign: (modelId: string) => void;
  onUnassign: () => void;
}) {
  const [selectedModel, setSelectedModel] = useState<string>(activeModel);

  useEffect(() => { setSelectedModel(activeModel); }, [activeModel]);
  useEffect(() => {
    if (models === null) onLoadModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = Array.isArray(models) ? models : null;

  return (
    <li className={`ait-office__card${active ? ' ait-office__card--active' : ''}`}>
      <div className="ait-office__card-icon" aria-hidden>
        <ProviderIcon vendor={candidate.vendor} size={30} />
      </div>
      <div className="ait-office__card-body">
        <span className="ait-office__card-name">{candidate.name}</span>
        <span className="ait-office__card-detail" title={candidate.id}>{candidate.detail}</span>
      </div>

      <div className="ait-office__card-model">
        {models === 'loading' && <span className="settings-muted">모델 조회 중…</span>}
        {models === 'error' && <span className="settings-error">모델 조회 실패</span>}
        {models === 'unsupported' && <span className="settings-muted">모델 선택 미지원</span>}
        {options && options.length === 0 && <span className="settings-muted">모델 목록 없음</span>}
        {options && options.length > 0 && (
          <StyledSelect
            value={selectedModel}
            disabled={busy}
            portal
            onChange={setSelectedModel}
            placeholder="provider 기본값"
            options={[
              { value: '', label: 'provider 기본값' },
              ...options.map((m) => ({ value: m.id, label: m.name })),
            ]}
          />
        )}
      </div>

      {active ? (
        <button type="button" className="ait-office__card-btn ait-office__card-btn--off" disabled={busy} onClick={onUnassign}>
          {busy ? '저장 중…' : '지정 해제'}
        </button>
      ) : (
        <button type="button" className="ait-office__card-btn" disabled={busy} onClick={() => onAssign(selectedModel)}>
          {busy ? '저장 중…' : '오피스에 지정'}
        </button>
      )}
    </li>
  );
}
